import type { StripeProduct } from '../types/crm';

/**
 * Convertit le unit_amount d'un StripeProduct en montant mensuel (en euros).
 * Gère correctement tous les intervalles : mensuel, semestriel, annuel, etc.
 * 
 * Exemples :
 * - 720€/mois (interval='month', interval_count=1) → 720
 * - 2298€/6 mois (interval='month', interval_count=6) → 383
 * - 8640€/an (interval='year', interval_count=1) → 720
 *
 * La définition de référence est en base : `stripe_product_monthly_amount(stripe_products)`,
 * qui dérive le MRR/ARR d'une opportunité. Ce miroir ne sert qu'à l'affichage du catalogue
 * et au MRR des abonnements ; il ne doit jamais être recopié dans `crm_opportunities`.
 * Mêmes cas de test des deux côtés (pricing.test.ts, supabase/tests/opportunity_mrr_and_task_completion.sql).
 */
export function getMonthlyAmount(product: StripeProduct): number {
  // One-time products are not recurring revenue — don't count as MRR
  if (product.interval === 'one_time') return 0;

  const totalMonths = product.interval === 'year'
    ? (product.interval_count || 1) * 12
    : (product.interval_count || 1);

  return product.unit_amount / totalMonths / 100;
}

/**
 * Retourne un label lisible pour l'intervalle de facturation.
 * Ex: "mois", "6 mois", "an", "2 ans"
 */
export function getIntervalLabel(product: StripeProduct): string {
  const count = product.interval_count || 1;
  if (product.interval === 'year') {
    return count === 1 ? 'an' : `${count} ans`;
  }
  if (count === 1) return 'mois';
  if (count === 6) return 'semestre';
  if (count === 3) return 'trimestre';
  return `${count} mois`;
}
